import { Component, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Portfolio from "./pages/Portfolio";
import Process from "./pages/Process";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Admin from "./pages/Admin";
import { EASE } from "./components/Reveal";

class ErrorBoundary extends Component {
  state = { err: null };
  static getDerivedStateFromError(err) {
    return { err };
  }
  render() {
    if (this.state.err) {
      return (
        <div className="min-h-screen grid place-items-center bg-ivory px-6 text-center">
          <div>
            <p className="font-serif text-3xl text-ink">Something went adrift.</p>
            <button
              data-testid="error-reload-button"
              onClick={() => window.location.reload()}
              className="btn-bronze mt-6"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function WhatsAppFloat() {
  return (
    <a
      href="https://wa.me/919494364449?text=Hello%20Aurea%20Living%2C%20I%20would%20like%20to%20discuss%20an%20interior%20design%20project."
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-button"
      aria-label="Chat with Aurea Living on WhatsApp"
      className="fixed bottom-5 right-5 z-40 grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-white shadow-xl shadow-black/20 transition-transform duration-300 hover:scale-110"
    >
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
}

function IntroCurtain() {
  return (
    <motion.div
      data-testid="intro-curtain"
      className="fixed inset-0 z-[100] bg-espresso flex flex-col items-center justify-center"
      exit={{ y: "-100%" }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
    >
      <motion.img
        src="/aurea-mark-gold.png"
        alt=""
        className="h-24 sm:h-28 mb-8 object-contain"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
      />
      <motion.div
        className="flex overflow-hidden"
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.05, delayChildren: 0.15 } } }}
      >
        {"AUREA LIVING".split("").map((ch, i) =>
          ch === " " ? (
            <span key={i} className="w-3 sm:w-5" />
          ) : (
            <motion.span
              key={i}
              variants={{ hidden: { y: "115%" }, show: { y: "0%" } }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-serif font-light text-3xl sm:text-5xl tracking-[0.18em] text-ivory"
            >
              {ch}
            </motion.span>
          )
        )}
      </motion.div>
      <motion.div
        className="mt-6 h-px bg-gold"
        initial={{ width: 0 }}
        animate={{ width: 120 }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
      />
      <motion.p
        className="mt-4 text-[10px] font-mono uppercase tracking-[0.35em] text-gold"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 0.5 }}
      >
        Hyderabad
      </motion.p>
    </motion.div>
  );
}

function App() {
  const [intro, setIntro] = useState(() => !sessionStorage.getItem("aurea_intro"));

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let frame = requestAnimationFrame(function raf(t) {
      lenis.raf(t);
      frame = requestAnimationFrame(raf);
    });
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (!intro) return;
    const t = setTimeout(() => {
      sessionStorage.setItem("aurea_intro", "1");
      setIntro(false);
    }, 2200);
    return () => clearTimeout(t);
  }, [intro]);

  useEffect(() => {
    document.body.style.overflow = intro ? "hidden" : "";
  }, [intro]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-ivory">
          <Navbar />
          <AnimatePresence>{intro && <IntroCurtain />}</AnimatePresence>
          <main>
            <Routes>
              <Route path="/" element={<Home heroDelay={intro ? 2.15 : 0.35} />} />
              <Route path="/services" element={<Services />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/process" element={<Process />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<Home heroDelay={0.35} />} />
            </Routes>
          </main>
          <Footer />
          <WhatsAppFloat />
          <div className="grain-overlay" aria-hidden="true" />
          <Toaster position="bottom-center" />
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
